import React from 'react'
import { Routes, Route, Navigate } from 'react-router-dom'
import { AuthProvider } from './hooks/useAuth'
import { AppProvider, useApp } from './hooks/useApp'
import { BrandingProvider } from './contexts/BrandingContext'
import Layout from './components/Layout'
import LoginPage from './pages/LoginPage'
import SignupPage from './pages/SignupPage'
import ForgotPasswordPage from './pages/ForgotPasswordPage'
import ResetPasswordPage from './pages/ResetPasswordPage'
import AuthCallbackPage from './pages/AuthCallbackPage'
import DashboardPage from './pages/DashboardPage'
import SubscriptionManagementPage from './pages/SubscriptionManagementPage'
import AdminDashboardPage from './pages/admin/AdminDashboardPage'
import AdminJobsPage from './pages/admin/AdminJobsPage'
import AdminSystemPage from './pages/admin/AdminSystemPage'
import AdminEmailSequencesPage from './pages/admin/AdminEmailSequencesPage'
import AdminEmailTemplatesPage from './pages/admin/AdminEmailTemplatesPage'
import AdminEmailTemplateEditPage from './pages/admin/AdminEmailTemplateEditPage'
import { ActionsDashboard } from './pages/ActionsDashboard'
import UserProgressView from './components/UserProgressView'
import { AdminPrompts } from './pages/AdminPrompts'
import ShareManagement from './pages/ShareManagement'
import SharedView from './pages/SharedView'
import SharedDashboard from './components/SharedDashboard'
import CitationTrackerOnboardingPage from './pages/CitationTrackerOnboardingPage'
import CitationTrackerDashboardPage from './pages/CitationTrackerDashboardPage'
import ProtectedRoute from './components/ProtectedRoute'
import AdminRoute from './components/AdminRoute'
import NotificationModal from './components/NotificationModal'

function AppContent() {
  const { notifications, loadJobsHistory } = useApp();
  const { modalNotification, hideModalNotification } = notifications;

  const handleCloseNotification = () => {
    hideModalNotification();
    loadJobsHistory({ bumpRevision: true });
  };

  return (
    <div className="min-h-screen hero-background">
      <Routes>
        {/* Public routes */}
        <Route path="/login" element={<LoginPage />} />
        <Route path="/signup" element={<SignupPage />} />
        <Route path="/forgot-password" element={<ForgotPasswordPage />} />
        <Route path="/reset-password" element={<ResetPasswordPage />} />
        <Route path="/auth/callback" element={<AuthCallbackPage />} />
        <Route path="/share/:token" element={<SharedView />} />
        <Route path="/shared/:token" element={<SharedDashboard />} />
        
        {/* Protected routes */}
        <Route path="/" element={
          <ProtectedRoute>
            <Layout>
              <DashboardPage />
            </Layout>
          </ProtectedRoute>
        } />
        
        <Route path="/account" element={<Navigate to="/" replace />} />
        
        <Route path="/account/subscription" element={
          <ProtectedRoute>
            <Layout>
              <SubscriptionManagementPage />
            </Layout>
          </ProtectedRoute>
        } />
        
        <Route path="/account/upgrade" element={
          <ProtectedRoute>
            <Layout>
              <SubscriptionManagementPage />
            </Layout>
          </ProtectedRoute>
        } />
        
        <Route path="/account/subscription-success" element={
          <ProtectedRoute>
            <Layout>
              <SubscriptionManagementPage />
            </Layout>
          </ProtectedRoute>
        } />
        
        <Route path="/citation-tracker" element={
          <ProtectedRoute>
            <Layout>
              <CitationTrackerDashboardPage />
            </Layout>
          </ProtectedRoute>
        } />
        
        <Route path="/citation-tracker/onboard" element={
          <ProtectedRoute>
            <Layout>
              <CitationTrackerOnboardingPage />
            </Layout>
          </ProtectedRoute>
        } />
        
        <Route path="/app" element={<Navigate to="/" replace />} />
        
        <Route path="/dashboard/actions" element={
          <ProtectedRoute>
            <Layout>
              <ActionsDashboard />
            </Layout>
          </ProtectedRoute>
        } />
        
        <Route path="/dashboard/shares" element={
          <ProtectedRoute>
            <Layout>
              <ShareManagement />
            </Layout>
          </ProtectedRoute>
        } />
        
        {/* Admin Routes - New Structure */}
        <Route path="/admin" element={
          <AdminRoute>
            <Layout>
              <AdminDashboardPage />
            </Layout>
          </AdminRoute>
        } />
        
        <Route path="/admin/jobs" element={
          <AdminRoute>
            <Layout>
              <AdminJobsPage />
            </Layout>
          </AdminRoute>
        } />
        
        <Route path="/admin/system" element={
          <AdminRoute>
            <Layout>
              <AdminSystemPage />
            </Layout>
          </AdminRoute>
        } />
        
        <Route path="/admin/email-sequences" element={
          <AdminRoute>
            <Layout>
              <AdminEmailSequencesPage />
            </Layout>
          </AdminRoute>
        } />
        
        <Route path="/admin/email-sequences/:sequenceId/templates" element={
          <AdminRoute>
            <Layout>
              <AdminEmailTemplatesPage />
            </Layout>
          </AdminRoute>
        } />
        
        <Route path="/admin/email-sequences/:sequenceId/templates/create" element={
          <AdminRoute>
            <Layout>
              <AdminEmailTemplateEditPage />
            </Layout>
          </AdminRoute>
        } />
        
        <Route path="/admin/email-sequences/:sequenceId/templates/:templateId/edit" element={
          <AdminRoute>
            <Layout>
              <AdminEmailTemplateEditPage />
            </Layout>
          </AdminRoute>
        } />
        
        <Route path="/admin/users" element={
          <AdminRoute>
            <Layout>
              <UserProgressView />
            </Layout>
          </AdminRoute>
        } />
        
        <Route path="/admin/prompts" element={
          <AdminRoute>
            <Layout>
              <AdminPrompts />
            </Layout>
          </AdminRoute>
        } />
        
        {/* Catch all - redirect to login */}
        <Route path="*" element={<Navigate to="/login" replace />} />
      </Routes>

      {/* Global Notification Modal */}
      <NotificationModal
        isOpen={!!modalNotification}
        onClose={handleCloseNotification}
        notification={modalNotification}
      />
    </div>
  );
}

function App() {
  return (
    <BrandingProvider>
      <AuthProvider>
        <AppProvider>
          <AppContent />
        </AppProvider>
      </AuthProvider>
    </BrandingProvider>
  )
}

export default App